import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Bell, CheckCircle2, Clock, Plus, Search, Users } from "lucide-react";
import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { SurveyResultsCard } from "@/components/surveys/survey-results-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useSurveyAccess } from "@/hooks/use-survey-access";
import { useToast } from "@/hooks/use-toast";
import { formatDate, formatDateTime } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { matchesSearch } from "@/lib/search-utils";

export default function SurveyInstanceDetail() {
  const [, params] = useRoute("/surveys/instances/:id");
  const [, setLocation] = useLocation();
  const { canOperateSurveys } = useSurveyAccess();
  const instanceId = params?.id;
  const [resetTarget, setResetTarget] = useState<{ id: string; name: string } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [remindOpen, setRemindOpen] = useState(false);
  const { toast } = useToast();

  const remindMutation = useMutation({
    mutationFn: async () => rpcClient.pulseSurveys.remindPending({ id: instanceId! }),
    onSuccess: (result) => {
      const sent = result.notificationsSent;
      toast({
        title: `${sent} ${sent === 1 ? "colaborador notificado" : "colaboradores notificados"}`,
        description:
          result.skipped > 0 || result.errors.length > 0
            ? [
                result.skipped > 0 ? `${result.skipped} já lembrado(s) hoje` : null,
                result.errors.length > 0
                  ? `${result.errors.length} sem envio (ex.: sem Slack)`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : undefined,
      });
    },
    onError: () => {
      toast({ title: "Erro ao enviar lembretes", variant: "destructive" });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async (responseId: string) => {
      await rpcClient.pulseSurveys.resetResponse({ id: responseId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.pulseSurveys.getInstanceDetail.queryOptions({ input: { id: instanceId! } })
          .queryKey,
      });
      toast({ title: "Resposta resetada com sucesso" });
    },
    onError: () => {
      toast({ title: "Erro ao resetar resposta", variant: "destructive" });
    },
  });

  const eligibleQuery = useQuery({
    ...orpc.pulseSurveys.listEligibleParticipants.queryOptions({ input: { id: instanceId! } }),
    enabled: !!instanceId && addOpen && canOperateSurveys,
  });

  const addParticipantsMutation = useMutation({
    mutationFn: async (employeeIds: string[]) => {
      return rpcClient.pulseSurveys.addInstanceParticipants({
        id: instanceId!,
        employeeIds,
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: orpc.pulseSurveys.getInstanceDetail.queryOptions({ input: { id: instanceId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.pulseSurveys.listEligibleParticipants.queryOptions({
          input: { id: instanceId! },
        }).queryKey,
      });
      toast({
        title: `${result.added} ${result.added === 1 ? "pessoa adicionada" : "pessoas adicionadas"}`,
      });
      setAddOpen(false);
      setSelectedIds(new Set());
      setAddSearch("");
    },
    onError: () => {
      toast({ title: "Erro ao adicionar pessoas", variant: "destructive" });
    },
  });

  const { data, isLoading } = useQuery({
    ...(canOperateSurveys
      ? orpc.pulseSurveys.getInstanceDetail.queryOptions({ input: { id: instanceId! } })
      : orpc.pulseSurveys.managerInstanceDetail.queryOptions({ input: { id: instanceId! } })),
    enabled: !!instanceId,
  });

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <p className="text-muted-foreground">Instância não encontrada</p>
        <Button variant="outline" className="mt-4" onClick={() => setLocation("/surveys")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
      </div>
    );
  }

  // Whether the answers are in the payload at all is the server's call: the
  // Operador de Pesquisas gets them stripped and `canViewResponses: false`.
  const { instance, survey, responses, canViewResponses } = data;
  const allResponses = responses || [];
  const finishedResponses = allResponses.filter(
    (r) => r.status === "completed" || r.status === "finished",
  );
  const pendingResponses = allResponses.filter((r) => r.status === "pending");
  const totalExpected = allResponses.length;
  const finishedCount = finishedResponses.length;
  const pendingCount = pendingResponses.length;
  const responseRate = totalExpected > 0 ? Math.round((finishedCount / totalExpected) * 100) : 0;

  const instanceStatus = instance.status;
  // Who the reminder would actually reach, computed server-side from the same
  // query the sender uses (excludes people who left; splits out no-Slack).
  const pendingReminder = data.pendingReminder;
  const reminderRecipients = pendingReminder?.recipients ?? 0;
  const reminderWithoutSlack = pendingReminder?.withoutSlack ?? 0;

  const eligibleList = (eligibleQuery.data ?? []).filter((e) =>
    matchesSearch(e.fullName, addSearch),
  );

  return (
    <div className="flex-1 space-y-6">
      <PageHeader
        title={survey.title}
        description={survey.description || "Pesquisa de clima organizacional"}
        onBack={() => setLocation(`/surveys/${survey.id}`)}
        titleExtra={<StatusBadge status={instanceStatus} />}
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--forest-pulse))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {finishedCount}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Respostas</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <Clock className="h-5 w-5 text-[hsl(var(--amber-state))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {pendingCount}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Pendentes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <Users className="h-5 w-5 text-[hsl(var(--ink-primary))]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                  {responseRate}%
                </p>
              </div>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Taxa de resposta</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <SurveyResultsCard
            responses={allResponses}
            questionInstances={data.questionInstances ?? []}
            canViewResponses={canViewResponses}
            canOperateSurveys={canOperateSurveys}
            onResetResponse={setResetTarget}
            resetPending={resetMutation.isPending}
          />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Status</p>
                <StatusBadge status={instanceStatus} />
              </div>
              <Separator />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Enviado em</p>
                <p className="text-sm">{formatDateTime(instance.sentAt)}</p>
              </div>
              <Separator />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Prazo</p>
                <p className="text-sm">{formatDate(instance.deadline)}</p>
              </div>
            </CardContent>
          </Card>

          {(pendingCount > 0 || (canOperateSurveys && instanceStatus !== "cancelled")) && (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[hsl(var(--amber-state))]" />
                      Aguardando resposta
                    </CardTitle>
                    <CardDescription className="mt-1.5">
                      {pendingCount === 0
                        ? "Ninguém pendente"
                        : `${pendingCount} colaborador${pendingCount > 1 ? "es" : ""} ainda não respondeu`}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {canOperateSurveys &&
                      instanceStatus === "active" &&
                      reminderRecipients + reminderWithoutSlack > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRemindOpen(true)}
                          disabled={remindMutation.isPending}
                          data-testid="button-remind-pending"
                        >
                          <Bell className="h-4 w-4 mr-1" />
                          {remindMutation.isPending ? "Enviando..." : "Lembrar pendentes"}
                        </Button>
                      )}
                    {canOperateSurveys && instanceStatus !== "cancelled" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAddOpen(true)}
                        data-testid="button-add-participants"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {pendingResponses.map((response) => (
                  <div
                    key={response.id}
                    className="flex items-center gap-3 rounded-md bg-[hsl(var(--paper-2))] p-3"
                    data-testid={`pending-${response.employeeId}`}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-[hsl(var(--comp-coral-mist))] text-[hsl(var(--ink-primary))] text-xs">
                        {getInitials(response.employee?.fullName || "?")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {response.employee?.fullName || "Colaborador"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {response.employee?.department || ""}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open);
          if (!open) {
            setSelectedIds(new Set());
            setAddSearch("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar pessoas</DialogTitle>
            <DialogDescription>
              As pessoas selecionadas serão incluídas na pesquisa sem receber notificação.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar colaborador..."
                value={addSearch}
                onChange={(e) => setAddSearch(e.target.value)}
                className="pl-9"
                data-testid="input-search-eligible"
              />
            </div>
            <div className="max-h-[360px] overflow-y-auto space-y-1 border rounded-md p-1">
              {eligibleQuery.isLoading ? (
                <p className="text-sm text-muted-foreground text-center py-6">Carregando...</p>
              ) : eligibleList.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Nenhum colaborador disponível
                </p>
              ) : (
                eligibleList.map((emp) => {
                  const checked = selectedIds.has(emp.id);
                  return (
                    <label
                      key={emp.id}
                      className="flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-[hsl(var(--sand-step))]"
                      data-testid={`eligible-${emp.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (value) next.add(emp.id);
                            else next.delete(emp.id);
                            return next;
                          });
                        }}
                      />
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {getInitials(emp.fullName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{emp.fullName}</p>
                        {emp.department && (
                          <p className="text-xs text-muted-foreground truncate">{emp.department}</p>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => addParticipantsMutation.mutate(Array.from(selectedIds))}
              disabled={selectedIds.size === 0 || addParticipantsMutation.isPending}
              data-testid="button-confirm-add-participants"
            >
              {addParticipantsMutation.isPending
                ? "Adicionando..."
                : `Adicionar${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={remindOpen} onOpenChange={setRemindOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lembrar pendentes?</AlertDialogTitle>
            <AlertDialogDescription>
              {reminderRecipients === 0
                ? "Nenhum pendente tem Slack vinculado, então ninguém vai receber o lembrete."
                : `${reminderRecipients} ${reminderRecipients === 1 ? "colaborador ainda não respondeu e vai" : "colaboradores ainda não responderam e vão"} receber um lembrete no Slack.`}
              {reminderWithoutSlack > 0 &&
                ` ${reminderWithoutSlack} ${reminderWithoutSlack === 1 ? "pendente não tem" : "pendentes não têm"} Slack vinculado e não ${reminderWithoutSlack === 1 ? "será lembrado" : "serão lembrados"}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remindMutation.mutate()}
              disabled={reminderRecipients === 0}
              data-testid="button-confirm-remind-pending"
            >
              Enviar lembrete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!resetTarget}
        onOpenChange={(open) => {
          if (!open) setResetTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resetar resposta?</AlertDialogTitle>
            <AlertDialogDescription>
              As respostas de {resetTarget?.name} serão apagadas e o status voltará para "pendente",
              permitindo que responda novamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (resetTarget) resetMutation.mutate(resetTarget.id);
                setResetTarget(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-reset"
            >
              Confirmar Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
